import React, { useEffect, useState } from "react";
import { Search, Plug, CheckCircle2, X, Loader2, PackagePlus, ExternalLink } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDropship() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [importing, setImporting] = useState(null); // pid being detailed/imported
  const [syncing, setSyncing] = useState(false);

  const loadStatus = () => api.get("/admin/cj/status").then((r) => setStatus(r.data)).catch(() => {});

  const syncVariants = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/cj/sync-variants");
      const d = r.data;
      const more = d.remaining_unsynced > 0 ? ` · ${d.remaining_unsynced} left — click again to continue` : " · all synced!";
      toast.success(`Synced ${d.updated} · ${d.flagged_needs_review} need variant review${d.errors ? ` · ${d.errors} errors` : ""}${more}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const connect = async () => {
    if (!apiKey.trim()) return;
    setConnecting(true);
    try {
      await api.post("/admin/cj/connect", { api_key: apiKey.trim() });
      toast.success("CJ Dropshipping connected!");
      setApiKey("");
      loadStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post("/admin/cj/disconnect");
      toast.success("Disconnected");
      setResults([]);
      loadStatus();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const search = async () => {
    setSearching(true);
    try {
      const r = await api.get("/admin/cj/search", { params: { keyword, page: 1, page_size: 20 } });
      setResults(r.data.products || []);
      if (!r.data.products?.length) toast.info("No products found. Try another keyword.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="admin-dropship">
      <h1 className="font-display text-4xl text-navy">CJ Dropshipping</h1>
      <p className="text-ink/60 mt-1">Connect your CJ account, search the catalog, and import products to your store.</p>

      {/* Connection card */}
      <div className="mt-6 bg-white rounded-lg border border-navy/15 p-5">
        {status === null ? (
          <div className="flex items-center gap-2 text-navy/50"><Loader2 className="w-4 h-4 animate-spin" /> Checking connection…</div>
        ) : status.connected ? (
          <div className="flex items-center justify-between flex-wrap gap-3" data-testid="cj-connected">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <div className="font-bold text-navy">Connected to CJ Dropshipping</div>
                <div className="text-sm text-ink/60">API key {status.api_key_masked}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="cj-sync-variants-btn" onClick={syncVariants} disabled={syncing}
                className="text-sm font-semibold text-navy border border-navy/30 rounded-md px-3 py-2 hover:bg-navy/5 inline-flex items-center gap-2 disabled:opacity-60">
                {syncing && <Loader2 className="w-4 h-4 animate-spin" />} Sync variants
              </button>
              <button data-testid="cj-disconnect-btn" onClick={disconnect}
                className="text-sm font-semibold text-brick border border-brick/40 rounded-md px-3 py-2 hover:bg-brick/5">
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div data-testid="cj-connect-form">
            <div className="flex items-center gap-2 font-bold text-navy"><Plug className="w-5 h-5" /> Connect your CJ account</div>
            <p className="text-sm text-ink/60 mt-1">
              Paste your CJ <b>API Key</b> (cjdropshipping.com → My CJ → Authorization → API → Generate).
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input
                data-testid="cj-api-key-input"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="5536321@api@xxxxxxxxxxxxxxxx"
                className="flex-1 min-w-[260px] rounded-md border border-navy/20 px-3 py-2 text-sm"
              />
              <button data-testid="cj-connect-btn" onClick={connect} disabled={connecting}
                className="bg-brick text-white font-semibold rounded-md px-5 py-2 disabled:opacity-60 inline-flex items-center gap-2">
                {connecting && <Loader2 className="w-4 h-4 animate-spin" />} Connect
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      {status?.connected && (
        <div className="mt-6">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-navy/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="cj-search-input"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="Search CJ catalog (e.g. phone case, dog toy)…"
                className="w-full rounded-md border border-navy/20 pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <button data-testid="cj-search-btn" onClick={search} disabled={searching}
              className="bg-navy text-white font-semibold rounded-md px-5 py-2 disabled:opacity-60 inline-flex items-center gap-2">
              {searching && <Loader2 className="w-4 h-4 animate-spin" />} Search
            </button>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cj-results">
            {results.map((p) => (
              <div key={p.pid} className="bg-white rounded-lg border border-navy/15 overflow-hidden flex flex-col">
                <div className="aspect-square bg-cream overflow-hidden">
                  {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="text-sm font-semibold text-navy line-clamp-2">{p.name}</div>
                  <div className="text-sm text-brick font-bold mt-1">{p.price != null ? `from ${money(p.price)}` : "—"}</div>
                  <button
                    data-testid={`cj-import-open-${p.pid}`}
                    onClick={() => setImporting(p.pid)}
                    className="mt-auto pt-3 text-sm font-semibold text-brick inline-flex items-center gap-1 hover:underline">
                    <PackagePlus className="w-4 h-4" /> Import
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {importing && (
        <ImportModal pid={importing} onClose={() => setImporting(null)} />
      )}
    </div>
  );
}

function ImportModal({ pid, onClose }) {
  const [detail, setDetail] = useState(null);
  const [vid, setVid] = useState("");
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [category, setCategory] = useState("Miscellaneous Treasures");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const loadDetail = () => {
    setLoadError(null);
    api.get(`/admin/cj/product/${pid}`)
      .then((r) => {
        setDetail(r.data);
        const v = r.data.variants?.[0];
        if (v) { setVid(v.vid); if (v.price != null) setPrice((v.price * 2).toFixed(2)); }
      })
      .catch((err) => setLoadError(formatApiError(err.response?.data?.detail)));
  };

  useEffect(() => { loadDetail(); }, [pid]);

  const selectedVariant = detail?.variants?.find((v) => v.vid === vid);

  const doImport = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.post("/admin/cj/import", {
        pid: detail.pid,
        vid: vid || "",
        name: detail.name,
        category: category,
        description: detail.description || "",
        images: detail.images || [],
        image: detail.images?.[0],
        supplier_cost: selectedVariant?.price ?? detail.price ?? null,
        price: price ? parseFloat(price) : null,
        sku: selectedVariant?.sku || "",
        variants: detail.variants || [],
      });
      toast.success("Imported to your store!");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cj-import-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-navy/10">
          <h2 className="font-display text-2xl text-navy">Import product</h2>
          <button onClick={onClose} data-testid="cj-import-close"><X className="w-5 h-5 text-navy/50" /></button>
        </div>
        {!detail ? (
          <div className="p-8 flex flex-col items-center justify-center gap-3 text-navy/50">
            {loadError ? (
              <>
                <p className="text-sm text-center text-ink/70">{loadError}</p>
                <button data-testid="cj-import-retry" onClick={loadDetail}
                  className="text-sm font-semibold bg-navy text-white rounded-md px-4 py-2">Retry</button>
              </>
            ) : (
              <Loader2 className="w-5 h-5 animate-spin" />
            )}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="flex gap-3">
              {detail.images?.[0] && <img src={detail.images[0]} alt="" className="w-24 h-24 object-cover rounded-md bg-cream" />}
              <div className="font-semibold text-navy">{detail.name}</div>
            </div>

            <div>
              <label className="block text-xs font-bold text-navy uppercase">Category (shown to customers)</label>
              <select data-testid="cj-import-category" value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="text-xs text-ink/50 mt-1">This is the label buyers see — pick a real category so it never shows "Drop-Ship".</p>
            </div>

            {detail.variants?.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-navy uppercase">Variant</label>
                <select data-testid="cj-import-variant" value={vid}
                  onChange={(e) => { setVid(e.target.value); const v = detail.variants.find((x) => x.vid === e.target.value); if (v?.price != null) setPrice((v.price * 2).toFixed(2)); }}
                  className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 text-sm bg-white">
                  {detail.variants.map((v) => (
                    <option key={v.vid} value={v.vid}>{v.name || v.sku} — cost {v.price != null ? money(v.price) : "?"}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-navy uppercase">Your cost</label>
                <div className="mt-1 px-3 py-2 text-sm bg-cream rounded-md">{selectedVariant?.price != null ? money(selectedVariant.price) : "—"}</div>
              </div>
              <div>
                <label className="block text-xs font-bold text-navy uppercase">Your sell price</label>
                <input data-testid="cj-import-price" type="number" step="0.01" value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 text-sm" placeholder="0.00" />
              </div>
            </div>
            {selectedVariant?.price != null && price && (
              <p className="text-sm text-ink/60">Profit per sale: <b className="text-green-700">{money(parseFloat(price) - selectedVariant.price)}</b></p>
            )}

            <button data-testid="cj-import-confirm" onClick={doImport} disabled={saving}
              className="w-full bg-brick text-white font-semibold rounded-md py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Import to my store
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
